import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import { auth, db } from "../firebaseConfig";
import { fetchClothes } from "../components/clothingService";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
    Calendar, 
    Star, 
    Plus, 
    Sparkles,
    ArrowRight,
    Shirt,
    Heart,
    Clock,
} from "lucide-react";
import InstallPWA from "../components/InstallPWA";
import ToastContainer from "../components/ToastContainer";
import useToast from "../hooks/useToast";
import { AuthContext } from "../context/AuthContext";
import { PLAN_LIMITS } from "../services/userService";
import { ALWAYS_PREMIUM } from "../config/featureFlags";
import { createPremiumCheckoutSession } from "../services/subscriptionService";
import "../styles/Dashboard.css";
import AddClothingModal from "../components/AddClothingModal";

const Dashboard = () => {
    const [username, setUsername] = useState("");
    const [totalClothes, setTotalClothes] = useState(0);
    const [monthlyClothes, setMonthlyClothes] = useState(0);
    const [randomFavoris, setRandomFavoris] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [planInfo, setPlanInfo] = useState(null);
    const [isSyncingSubscription, setIsSyncingSubscription] = useState(false);
    const [isStartingCheckout, setIsStartingCheckout] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const { toasts, removeToast, success, error: pushError } = useToast();
    const { userProfile } = useContext(AuthContext);
    const location = useLocation();
    const processedSessionRef = useRef(null);

    const loadUserData = useCallback(async (withLoading = true) => {
        if (!auth.currentUser) return;

        try {
            if (withLoading) {
                setIsLoading(true);
            }
            const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
            if (userDoc.exists()) {
                const data = userDoc.data();
                setUsername(data.username);
                setPlanInfo({
                    plan: data.plan || "free",
                    limits: data.limits || { dailyGenerations: 1 },
                    generationUsage: data.generationUsage || null,
                    subscriptionStatus: data.subscriptionStatus || (data.plan === "premium" ? "premium" : "free"),
                    stripeCustomerId: data.stripeCustomerId || null,
                    stripeSubscriptionId: data.stripeSubscriptionId || null,
                });
            }

            const clothes = await fetchClothes(auth.currentUser.uid);
            setTotalClothes(clothes.length);

            const currentMonth = new Date().getMonth();
            const monthly = clothes.filter(item =>
                new Date(item.createdAt?.toDate()).getMonth() === currentMonth
            );
            setMonthlyClothes(monthly.length);

            const favoris = clothes.filter(item => item.favori);
            const shuffled = favoris.sort(() => 0.5 - Math.random());
            setRandomFavoris(shuffled.slice(0, 4));

        } catch (error) {
            console.error("Erreur chargement données:", error);
            pushError("Erreur lors du chargement des données");
        } finally {
            if (withLoading) {
                setIsLoading(false);
            }
        }
    }, [pushError]);

    useEffect(() => {
        loadUserData(true);
    }, [loadUserData]);

    useEffect(() => {
        if (!userProfile) return;

        setUsername((prev) => prev || userProfile.username || "");
        setPlanInfo({
            plan: userProfile.plan || "free",
            limits: userProfile.limits || { dailyGenerations: 1 },
            generationUsage: userProfile.generationUsage || null,
            subscriptionStatus: userProfile.subscriptionStatus || (userProfile.plan === "premium" ? "premium" : "free"),
            stripeCustomerId: userProfile.stripeCustomerId || null,
            stripeSubscriptionId: userProfile.stripeSubscriptionId || null,
        });
    }, [userProfile]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");

        if (!sessionId || !auth.currentUser || isSyncingSubscription) {
            return;
        }

        if (processedSessionRef.current === sessionId) {
            return;
        }

        const syncSubscription = async () => {
            setIsSyncingSubscription(true);
            try {
                processedSessionRef.current = sessionId;
                const response = await fetch(`/api/checkout-session?session_id=${sessionId}`);
                if (!response.ok) {
                    const message = await response.text();
                    throw new Error(message || "Impossible de vérifier votre paiement.");
                }

                const data = await response.json();

                if (data?.client_reference_id && data.client_reference_id !== auth.currentUser.uid) {
                    throw new Error("La session Stripe ne correspond pas à votre compte.");
                }

                if (data?.subscription_status === "active") {
                    await setDoc(doc(db, "users", auth.currentUser.uid), {
                        plan: "premium",
                        subscriptionStatus: "premium",
                        limits: { ...PLAN_LIMITS.premium },
                        stripeCustomerId: data.customer || null,
                        stripeSubscriptionId: data.subscription || null,
                        updatedAt: serverTimestamp(),
                    }, { merge: true });

                    success("Abonnement Premium confirmé. Merci !");
                } else {
                    throw new Error("Le paiement n'a pas été confirmé. Contactez le support si besoin.");
                }
            } catch (error) {
                console.error("Erreur de synchronisation de l'abonnement :", error);
                processedSessionRef.current = null;
                pushError(error.message || "Impossible de confirmer votre abonnement.");
            } finally {
                setIsSyncingSubscription(false);
                params.delete("session_id");
                const newSearch = params.toString();
                const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}${location.hash}`;
                window.history.replaceState({}, "", newUrl);
            }
        };

        syncSubscription();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, auth.currentUser, isSyncingSubscription]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const todayKey = new Date().toISOString().split("T")[0];
    const dailyLimit = ALWAYS_PREMIUM ? null : (planInfo?.limits?.dailyGenerations ?? (planInfo?.plan === "premium" ? null : 1));
    const usageToday = planInfo?.generationUsage?.date === todayKey ? planInfo.generationUsage.count || 0 : 0;
    const remainingGenerations = dailyLimit === null ? null : Math.max(dailyLimit - usageToday, 0);

    const handleOpenAddModal = async () => {
        // Vérifier la limite AVANT d'ouvrir le modal pour éviter de gaspiller des tokens IA
        const plan = userProfile?.plan || planInfo?.plan || "free";
        // Important: ne pas écraser les valeurs null (illimité).
        let maxClothes;
        if (ALWAYS_PREMIUM) {
            maxClothes = null;
        } else {
            const userLimit = userProfile?.limits?.maxClothes;
            const planLimit = PLAN_LIMITS[plan]?.maxClothes;
            if (userLimit !== undefined) {
                maxClothes = userLimit; // null => illimité, nombre => limite
            } else if (planLimit !== undefined) {
                maxClothes = planLimit; // null => illimité, nombre => limite
            } else {
                maxClothes = PLAN_LIMITS.free.maxClothes; // fallback ultime
            }
        }

        if (maxClothes !== null && totalClothes >= maxClothes) {
            pushError(`Limite atteinte ! Vous avez déjà ${maxClothes} vêtements. Passez au Premium pour un ajout illimité.`);
            return;
        }
        
        setIsAddModalOpen(true);
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        loadUserData(false);
    };

    const handleStartPremiumCheckout = async () => {
        if (!auth.currentUser) {
            pushError("Veuillez vous reconnecter pour finaliser l'abonnement Premium.");
            return;
        }

        if (ALWAYS_PREMIUM) {
            success("Le mode Premium est déjà activé. Merci !");
            return;
        }

        setIsStartingCheckout(true);
        try {
            const url = await createPremiumCheckoutSession({
                user: auth.currentUser,
                metadata: { source: "dashboard_plan_card" },
            });
            window.location.href = url;
        } catch (error) {
            console.error("Erreur lors du démarrage du checkout Stripe :", error);
            pushError(error.message || "Impossible de lancer le paiement. Réessayez dans un instant.");
        } finally {
            setIsStartingCheckout(false);
        }
    };

    const statsCards = [
        {
            title: "Vêtements",
            value: totalClothes,
            icon: Shirt,
            color: "from-blue-500 to-blue-600",
            bgColor: "bg-blue-50",
            textColor: "text-blue-600"
        },
        {
            title: "Ce mois",
            value: monthlyClothes,
            icon: Calendar,
            color: "from-emerald-500 to-emerald-600",
            bgColor: "bg-emerald-50",
            textColor: "text-emerald-600"
        },
        {
            title: "Favoris",
            value: randomFavoris.length,
            icon: Heart,
            color: "from-rose-500 to-rose-600",
            bgColor: "bg-rose-50",
            textColor: "text-rose-600"
        }
    ];

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center space-y-6"
                >
                    <div className="relative">
                        <div className="w-16 h-16 border-4 border-gray-100 rounded-full"></div>
                        <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-black rounded-full animate-spin"></div>
                    </div>
                    <div className="text-center">
                        <motion.p 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-gray-600 font-medium text-lg"
                        >
                            Chargement de votre dressing...
                        </motion.p>
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: "100%" }}
                            transition={{ delay: 0.5, duration: 1.5 }}
                            className="h-1 bg-gray-200 rounded-full mt-2 overflow-hidden"
                        >
                            <div className="h-full bg-black rounded-full animate-pulse"></div>
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <AddClothingModal
                isOpen={isAddModalOpen}
                onClose={handleCloseAddModal}
                initialData={null}
                isEditing={false}
            />
            <Sidebar />
            
            <main className="ml-0 md:ml-24 px-4 py-6 md:px-8 pb-20 md:pb-6">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="max-w-7xl mx-auto"
                >
                    {/* Header */}
                    <motion.div variants={itemVariants} className="mb-8">
                        <Link to="/" className="inline-block mb-6">
                            <div className="flex items-center space-x-3">
                                <img src="/Logo1.png" alt="DressCode Logo" className="h-10 md:h-12" />
                                <img src="/Logo2.png" alt="DressCode Subtitle Logo" className="h-3 md:h-4" />
                            </div>
                        </Link>
                        
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                            <div>
                                <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">
                                    Bonjour, {username}
                                </h1>
                                <p className="text-gray-600 text-lg">
                                    Prêt à organiser ton style aujourd'hui ?
                                </p>
                            </div>
                            
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="mt-4 md:mt-0"
                            >
                                <button
                                    type="button"
                                    onClick={handleOpenAddModal}
                                    className="inline-flex items-center px-6 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
                                >
                                    <Plus className="w-5 h-5 mr-2" />
                                    Ajouter un vêtement
                                </button>
                            </motion.div>
                        </div>
                    </motion.div>

                    {planInfo && (
                        <motion.section variants={itemVariants} className="mb-8">
                            <div className="rounded-2xl border border-gray-200 bg-gradient-to-r from-gray-50 to-white p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div>
                                    <p className="text-sm uppercase tracking-[0.2em] text-gray-500">Plan actuel</p>
                                    <h2 className="text-2xl font-bold text-gray-900 mt-1">
                                        {planInfo.plan === "premium" ? "Premium" : "Gratuit"}
                                    </h2>
                                    <p className="text-gray-600 mt-2">
                                        {planInfo.plan === "premium"
                                            ? "Générations IA illimitées et accès complet aux nouveautés."
                                            : remainingGenerations === 0
                                                ? "Vous avez utilisé votre génération IA du jour. Passez au Premium pour en profiter sans limite."
                                                : `Encore ${remainingGenerations} génération${remainingGenerations > 1 ? "s" : ""} IA disponible${remainingGenerations > 1 ? "s" : ""} aujourd'hui.`}
                                    </p>
                                    {planInfo.plan !== "premium" && dailyLimit !== null && (
                                        <p className="text-sm text-gray-500 mt-1">
                                            Limite quotidienne : {dailyLimit} génération{dailyLimit > 1 ? "s" : ""}.
                                        </p>
                                    )}
                                    {isSyncingSubscription && (
                                        <p className="text-sm text-blue-600 mt-2">
                                            Validation de votre paiement en cours...
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    {planInfo.plan === "premium" ? (
                                        <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-600 text-white font-medium">
                                            Premium actif
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleStartPremiumCheckout}
                                            disabled={isStartingCheckout || isSyncingSubscription}
                                            className="inline-flex items-center px-4 py-2 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            {isStartingCheckout ? "Redirection..." : "Passer au Premium"}
                                            {!isStartingCheckout && <ArrowRight className="w-4 h-4 ml-2" />}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.section>
                    )}

                    {/* Stats Cards */}
                    <motion.section variants={itemVariants} className="mb-12">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {statsCards.map((card, index) => {
                                const IconComponent = card.icon;
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        whileHover={{ 
                                            y: -8, 
                                            scale: 1.02,
                                            transition: { duration: 0.2 }
                                        }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`${card.bgColor} rounded-2xl p-6 border border-gray-100 shadow-soft hover:shadow-medium transition-all duration-300 cursor-pointer`}
                                    >
                                        <div className="flex items-center justify-between mb-4">
                                            <motion.div 
                                                className={`p-3 rounded-xl bg-gradient-to-r ${card.color} shadow-sm`}
                                                whileHover={{ rotate: 5, scale: 1.1 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <IconComponent className="w-6 h-6 text-white" />
                                            </motion.div>
                                            <motion.div
                                                animate={{ rotate: [0, 10, -10, 0] }}
                                                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                                            >
                                                <Sparkles className="w-5 h-5 text-gray-400" />
                                            </motion.div>
                                        </div>
                                        <div>
                                            <motion.p 
                                                className={`text-3xl font-bold ${card.textColor} mb-1`}
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{ delay: index * 0.1 + 0.3, type: "spring", stiffness: 200 }}
                                            >
                                                {card.value}
                                            </motion.p>
                                            <p className="text-gray-600 font-medium">{card.title}</p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.section>

                    {/* Quick Actions */}
                    <motion.section variants={itemVariants} className="mb-12">
                        <motion.h2 
                            className="text-xl font-bold text-gray-900 mb-6"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            Actions rapides
                        </motion.h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { title: "Dressing", icon: Shirt, link: "/dressing", color: "bg-blue-50 text-blue-600 hover:bg-blue-100" },
                                { title: "Suggestions", icon: Sparkles, link: "/suggestions", color: "bg-purple-50 text-purple-600 hover:bg-purple-100" },
                                { title: "Tenues du jour", icon: Clock, link: "/outfits", color: "bg-green-50 text-green-600 hover:bg-green-100" },
                                { title: "Paramètres", icon: Star, link: "/settings", color: "bg-orange-50 text-orange-600 hover:bg-orange-100" }
                            ].map((action, index) => {
                                const IconComponent = action.icon;
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 + index * 0.1 }}
                                        whileHover={{ 
                                            scale: 1.05, 
                                            y: -2,
                                            transition: { duration: 0.2 }
                                        }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <Link
                                            to={action.link}
                                            className={`${action.color} rounded-xl p-4 block text-center shadow-soft hover:shadow-medium transition-all duration-300`}
                                        >
                                            <motion.div
                                                whileHover={{ rotate: 5, scale: 1.1 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <IconComponent className="w-8 h-8 mx-auto mb-2" />
                                            </motion.div>
                                            <p className="font-medium text-sm">{action.title}</p>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.section>

                    {/* Favorites Section */}
                    <motion.section variants={itemVariants}>
                        <motion.div 
                            className="flex items-center justify-between mb-6"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            <h2 className="text-xl font-bold text-gray-900">Tes favoris</h2>
                            <motion.div
                                whileHover={{ x: 4 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Link
                                    to="/dressing"
                                    className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    Voir tout
                                    <ArrowRight className="w-4 h-4 ml-1" />
                                </Link>
                            </motion.div>
                        </motion.div>
                        
                        <AnimatePresence mode="wait">
                            {randomFavoris.length > 0 ? (
                                <motion.div 
                                    className="grid grid-cols-2 md:grid-cols-4 gap-4"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {randomFavoris.map((item, index) => (
                                        <motion.div
                                            key={item.id}
                                            initial={{ opacity: 0, scale: 0.8, y: 20 }}
                                            animate={{ opacity: 1, scale: 1, y: 0 }}
                                            exit={{ opacity: 0, scale: 0.8, y: -20 }}
                                            transition={{ 
                                                delay: index * 0.1,
                                                type: "spring",
                                                stiffness: 100
                                            }}
                                            whileHover={{ 
                                                y: -8, 
                                                scale: 1.02,
                                                transition: { duration: 0.2 }
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            className="bg-white rounded-xl overflow-hidden shadow-soft hover:shadow-medium border border-gray-100 transition-all duration-300 cursor-pointer"
                                        >
                                            <div className="relative aspect-square overflow-hidden">
                                                {item.image ? (
                                                    <motion.img
                                                        src={item.image}
                                                        alt={item.type}
                                                        className="w-full h-full object-cover"
                                                        whileHover={{ scale: 1.1 }}
                                                        transition={{ duration: 0.3 }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                                        <Shirt className="w-8 h-8 text-gray-400" />
                                                    </div>
                                                )}
                                                <motion.div 
                                                    className="absolute top-2 right-2"
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ delay: index * 0.1 + 0.5, type: "spring" }}
                                                >
                                                    <div className="bg-yellow-400 p-1.5 rounded-full shadow-sm">
                                                        <Star className="w-3 h-3 text-white fill-current" />
                                                    </div>
                                                </motion.div>
                                            </div>
                                            <div className="p-3">
                                                <p className="font-medium text-gray-900 text-sm truncate">{item.type}</p>
                                                <p className="text-gray-500 text-xs capitalize">
                                                    {Array.isArray(item.colors) && item.colors.length > 0
                                                        ? item.colors.join(', ')
                                                        : item.color || "Non spécifiée"}
                                                </p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="text-center py-16"
                                >
                                    <motion.div
                                        animate={{ 
                                            scale: [1, 1.1, 1],
                                            rotate: [0, 5, -5, 0]
                                        }}
                                        transition={{ 
                                            duration: 2, 
                                            repeat: Infinity,
                                            repeatDelay: 3
                                        }}
                                    >
                                        <Heart className="w-16 h-16 text-gray-300 mx-auto mb-6" />
                                    </motion.div>
                                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucun favori pour le moment</h3>
                                    <p className="text-gray-500 mb-6">Commencez par ajouter des vêtements à vos favoris</p>
                                    <motion.div
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <Link
                                            to="/dressing"
                                            className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors shadow-soft"
                                        >
                                            <Plus className="w-5 h-5 mr-2" />
                                            Ajouter des favoris
                                        </Link>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.section>

                    <InstallPWA />
                </motion.div>
            </main>
            
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </div>
    );
};

export default Dashboard;
